import React, { useEffect, useState } from 'react';
import { Card, Table, Tag, Typography, Space, Statistic, Row, Col, Badge, Collapse, Empty, Spin, Alert, Modal, Button } from 'antd';
import { getData } from '../../../../../../actions/apiCall';
import { useDispatch } from 'react-redux';
import {
    ApiOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    WarningOutlined,
    UserOutlined,
    BugOutlined,
    ClockCircleOutlined,
    ExclamationCircleOutlined,
    EyeOutlined
} from '@ant-design/icons';
import moment from 'moment';

const { Title, Text } = Typography;
const { Panel } = Collapse;

interface TestLogsTabProps {
    testId: string | null;
}

interface LogEntry {
    _id: string;
    type: string;
    action: string;
    timestamp: string;
    cheatingType?: string;
    cheatingStatus?: string;
    apiUrl?: string;
    statusCode?: number;
    errorMessage?: string;
    duration?: number;
    metadata?: any;
}

const TestLogsTab: React.FC<TestLogsTabProps> = ({ testId }) => {
    const [logs, setLogs] = useState<LogEntry[]>([]);
    const [logsByType, setLogsByType] = useState<any>({});
    const [stats, setStats] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [metaModalVisible, setMetaModalVisible] = useState(false);
    const [selectedMeta, setSelectedMeta] = useState<any>(null);
    const dispatch = useDispatch();

    useEffect(() => {
        if (testId) {
            fetchLogs();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [testId]);

    const fetchLogs = async () => {
        if (!testId) return;

        setLoading(true);
        try {
            const resData = await dispatch(getData(`/test-setup/logs/${testId}`));
            if (resData?.code === 200) {
                setLogs(resData.data.logs || []);
                setLogsByType(resData.data.logsByType || {});
                setStats(resData.data.stats || null);
            }
        } catch (error) {
            console.error('Error fetching logs:', error);
        } finally {
            setLoading(false);
        }
    };

    const getLogIcon = (type: string) => {
        switch (type) {
            case 'api_call':
                return <ApiOutlined style={{ color: '#1890ff' }} />;
            case 'api_response':
                return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
            case 'api_error':
            case 'execution_error':
                return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
            case 'cheating_detected':
                return <WarningOutlined style={{ color: '#faad14' }} />;
            case 'user_action':
                return <UserOutlined style={{ color: '#722ed1' }} />;
            case 'system_event':
                return <BugOutlined style={{ color: '#13c2c2' }} />;
            case 'execution_start':
            case 'execution_end':
                return <ClockCircleOutlined style={{ color: '#52c41a' }} />;
            default:
                return <ExclamationCircleOutlined style={{ color: '#8c8c8c' }} />;
        }
    };

    const getLogColor = (type: string) => {
        switch (type) {
            case 'api_call':
                return 'blue';
            case 'api_response':
                return 'green';
            case 'api_error':
            case 'execution_error':
                return 'red';
            case 'cheating_detected':
                return 'orange';
            case 'user_action':
                return 'purple';
            case 'system_event':
                return 'cyan';
            case 'execution_start':
            case 'execution_end':
                return 'green';
            default:
                return 'default';
        }
    };

    const formatDuration = (ms: number) => {
        if (!ms) return '-';
        if (ms < 1000) return `${ms}ms`;
        return `${(ms / 1000).toFixed(2)}s`;
    };

    const columns = [
        {
            title: 'Time',
            dataIndex: 'timestamp',
            key: 'timestamp',
            width: 180,
            render: (timestamp: string) => (
                <Text type="secondary" style={{ fontSize: '12px' }}>
                    {moment(timestamp).format('YYYY-MM-DD HH:mm:ss')}
                </Text>
            ),
            sorter: (a: LogEntry, b: LogEntry) =>
                moment(a.timestamp).unix() - moment(b.timestamp).unix(),
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            width: 150,
            render: (type: string) => (
                <Tag color={getLogColor(type)} icon={getLogIcon(type)}>
                    {type.replace(/_/g, ' ').toUpperCase()}
                </Tag>
            ),
            filters: [
                { text: 'API Call', value: 'api_call' },
                { text: 'API Response', value: 'api_response' },
                { text: 'API Error', value: 'api_error' },
                { text: 'Execution', value: 'execution_start' },
                { text: 'Cheating', value: 'cheating_detected' },
                { text: 'User Action', value: 'user_action' },
            ],
            onFilter: (value: any, record: LogEntry) => record.type === value,
        },
        {
            title: 'Action',
            dataIndex: 'action',
            key: 'action',
            ellipsis: true,
            render: (action: string, record: LogEntry) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{action}</Text>
                    {record.apiUrl && (
                        <Text type="secondary" style={{ fontSize: '11px' }}>
                            {record.apiUrl}
                        </Text>
                    )}
                    {record.cheatingType && (
                        <Tag color="orange" style={{ marginTop: '4px' }}>
                            {record.cheatingType.replace(/_/g, ' ')}
                        </Tag>
                    )}
                </Space>
            ),
        },
        {
            title: 'Status',
            key: 'status',
            width: 120,
            render: (_: any, record: LogEntry) => {
                if (record.statusCode) {
                    const color = record.statusCode >= 200 && record.statusCode < 300 ? 'green' :
                        record.statusCode >= 400 ? 'red' : 'orange';
                    return <Tag color={color}>{record.statusCode}</Tag>;
                }
                if (record.cheatingStatus) {
                    return (
                        <Tag color={record.cheatingStatus === 'cheating' ? 'red' : 'green'}>
                            {record.cheatingStatus}
                        </Tag>
                    );
                }
                return <Tag>-</Tag>;
            },
        },
        {
            title: 'Duration',
            dataIndex: 'duration',
            key: 'duration',
            width: 100,
            render: (duration: number) => (
                <Text type="secondary">{formatDuration(duration)}</Text>
            ),
        },
        {
            title: 'Details',
            key: 'details',
            width: 150,
            render: (_: any, record: LogEntry) => {
                const hasDetails = record.errorMessage || (record.metadata && Object.keys(record.metadata).length > 0);
                if (!hasDetails) {
                    return <Text type="secondary">-</Text>;
                }
                return (
                    <Space>
                        {record.errorMessage && (
                            <Collapse ghost size="small">
                                <Panel header="View Error" key={`error-${record._id}`}>
                                    <Text type="danger">{record.errorMessage}</Text>
                                </Panel>
                            </Collapse>
                        )}
                        {record.metadata && Object.keys(record.metadata).length > 0 && (
                            <Button
                                type="link"
                                icon={<EyeOutlined />}
                                size="small"
                                onClick={() => {
                                    setSelectedMeta(record.metadata);
                                    setMetaModalVisible(true);
                                }}
                            >
                                View Meta
                            </Button>
                        )}
                    </Space>
                );
            },
        },
    ];

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '40px' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!testId) {
        return (
            <Empty description="No test ID provided" />
        );
    }

    if (logs.length === 0) {
        return (
            <Card>
                <Empty description="No logs found for this test" />
            </Card>
        );
    }

    return (
        <div style={{ padding: '24px' }}>
            {/* Statistics Cards */}
            {stats && (
                <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                    <Col xs={24} sm={12} md={6}>
                        <Card>
                            <Statistic
                                title="Total Logs"
                                value={stats.total}
                                prefix={<ApiOutlined />}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card>
                            <Statistic
                                title="API Calls"
                                value={stats.apiCalls}
                                prefix={<ApiOutlined />}
                                valueStyle={{ color: '#1890ff' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card>
                            <Statistic
                                title="API Errors"
                                value={stats.apiErrors}
                                prefix={<CloseCircleOutlined />}
                                valueStyle={{ color: '#ff4d4f' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card>
                            <Statistic
                                title="Cheating Events"
                                value={stats.cheatingEvents}
                                prefix={<WarningOutlined />}
                                valueStyle={{ color: '#faad14' }}
                            />
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Cheating Alert */}
            {stats && stats.cheatingCount > 0 && (
                <Alert
                    message={`${stats.cheatingCount} Cheating Event(s) Detected`}
                    description="This test has suspicious activity that may indicate cheating attempts."
                    type="warning"
                    showIcon
                    style={{ marginBottom: '24px' }}
                />
            )}

            {/* Logs Table */}
            <Card
                title={
                    <Space>
                        <Title level={5} style={{ margin: 0 }}>Test Activity Logs</Title>
                        <Badge count={logs.length} showZero style={{ backgroundColor: '#52c41a' }} />
                    </Space>
                }
            >
                <Table
                    dataSource={logs}
                    columns={columns}
                    rowKey="_id"
                    pagination={{
                        pageSize: 50,
                        showSizeChanger: true,
                        showTotal: (total) => `Total ${total} logs`,
                    }}
                    scroll={{ x: 1200 }}
                    size="small"
                />
            </Card>

            {/* Metadata Modal */}
            <Modal
                title="Metadata Details"
                open={metaModalVisible}
                onCancel={() => {
                    setMetaModalVisible(false);
                    setSelectedMeta(null);
                }}
                footer={[
                    <Button key="close" onClick={() => {
                        setMetaModalVisible(false);
                        setSelectedMeta(null);
                    }}>
                        Close
                    </Button>
                ]}
                width={800}
            >
                {selectedMeta && (
                    <pre style={{
                        background: '#f5f5f5',
                        padding: '16px',
                        borderRadius: '4px',
                        fontSize: '13px',
                        maxHeight: '60vh',
                        overflow: 'auto',
                        margin: 0,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                    }}>
                        {JSON.stringify(selectedMeta, null, 2)}
                    </pre>
                )}
            </Modal>
        </div>
    );
};

export default TestLogsTab;
